import * as InertiaReact from '@inertiajs/react';
import { publishedReact, runtimeElementType } from '@Pages/pageRuntime/compiler/runtimeReact';
import { FileImage } from '@Pages/pageRuntime/components/Image';
import { FIELD_BUILDER_TYPES } from '@Pages/pageRuntime/modules/fieldTypes.generated';
import { PAGE_SDK_EXPORTS } from '@Pages/pageRuntime/sdk/generated/sdkExports';
import ReactDOM from 'react-dom';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { Fragment, jsx, jsxs } from 'react/jsx-runtime';

/**
 * The page host of prebuilt workspace modules. The build compiles each import
 * of a host module into a read of `globalThis.__DIAGONAL_HOST__.modules`, so
 * every workspace module and package shares this document's React, React
 * DOM, Inertia, and Page SDK instances. The modules must match the
 * `HOST_MODULES` of scripts/lib/runtime-build/module-imports.mjs, and the
 * version must match the `HOST_API_VERSION` of
 * scripts/lib/runtime-build/esm-artifacts.mjs.
 */
export const PAGE_HOST_API_VERSION = 1;

type PageHost = {
  version: number;
  modules: Record<string, unknown>;
};

declare global {
  var __DIAGONAL_HOST__: PageHost | undefined;
}

let currentSdk: object | null = null;

/**
 * Binds the Page SDK of the runtime that renders the modules. One page
 * runtime renders the page and its layout, so modules read the SDK of the
 * page that is on screen.
 */
export function bindPageHostSdk(sdk: object): void {
  currentSdk = sdk;
}

function readySdk(): object {
  if (currentSdk === null) {
    throw new Error(
      'The Page SDK is not ready. Use Page SDK members inside a component, not when the module loads.'
    );
  }

  return currentSdk;
}

/**
 * A factory-style file reaches its custom components as `Sdk.Custom.Name`. The
 * build binds each one to the component's module, and any other name reads
 * the runtime's own registry, which names a missing component.
 */
function withCustom(components: Record<string, unknown>): object {
  const custom = new Proxy(
    {},
    {
      get: (_target, name) =>
        typeof name === 'string' && Object.hasOwn(components, name)
          ? components[name]
          : Reflect.get(Reflect.get(readySdk(), 'Custom') ?? {}, name)
    }
  );

  return new Proxy(
    {},
    {
      get: (_target, key) => (key === 'Custom' ? custom : Reflect.get(readySdk(), key))
    }
  );
}

const sdkModule = new Proxy(
  {},
  {
    get: (_target, key) => (key === 'withCustom' ? withCustom : Reflect.get(readySdk(), key))
  }
);

function readMember(path: readonly string[]): unknown {
  return path.reduce<unknown>(
    (value, key) =>
      typeof value === 'object' && value !== null ? Reflect.get(value, key) : undefined,
    readySdk()
  );
}

/**
 * The `@diagonal-hq/pages` module: one enumerable getter for each named
 * export, which reads the member of the SDK that is bound when it is read.
 * The build compiles a host import as CommonJS, and its interop copies these
 * own property names, so a named import reads the SDK at render time. `Sdk`
 * stays for the factory adapter, which the build injects.
 */
function pagesSdkModule(): object {
  const module = { Sdk: sdkModule };

  for (const [name, path] of Object.entries(PAGE_SDK_EXPORTS)) {
    Object.defineProperty(module, name, { enumerable: true, get: () => readMember(path) });
  }

  return module;
}

function hostElementType(
  type: React.ElementType,
  props: Record<string, unknown>
): React.ElementType {
  const elementType = runtimeElementType(type, props);

  return elementType === type ? type : FileImage;
}

const jsxRuntime = {
  Fragment,
  jsx: (type: React.ElementType, props: Record<string, unknown>, key?: string) =>
    jsx(hostElementType(type, props), props, key),
  jsxs: (type: React.ElementType, props: Record<string, unknown>, key?: string) =>
    jsxs(hostElementType(type, props), props, key)
};

const identity = <T>(value: T): T => value;

type FieldOptions = Record<string, unknown>;

const fieldOf =
  ($field: string) =>
  (options: FieldOptions = {}) => ({ $field, ...options });

/** The `field` helpers of packages/cli/fields.generated.js, from the same generated catalog. */
const field = Object.fromEntries(
  Object.entries(FIELD_BUILDER_TYPES).map(([name, type]) => [name, fieldOf(type)])
);

const runtimeHelpers = {
  definePage: (_page: unknown, component: unknown) => component,
  defineConfig: identity,
  defineResource: identity,
  defineNavigation: identity,
  defineNotificationType: identity,
  defineNotificationTemplate: identity,
  defineEmailLayout: identity,
  defineAssets: identity,
  asset: { version: (reference: string) => ({ $asset: 'version', reference }) },
  defineNotificationSchedule: identity,
  field
};

/** Installs the page host once per document, before the first workspace module loads. */
export function installPageHost(): void {
  if (globalThis.__DIAGONAL_HOST__ !== undefined) {
    return;
  }

  globalThis.__DIAGONAL_HOST__ = {
    version: PAGE_HOST_API_VERSION,
    modules: {
      react: { ...publishedReact, default: publishedReact },
      'react/jsx-runtime': jsxRuntime,
      'react-dom': ReactDOM,
      'react-dom/client': { createRoot, hydrateRoot },
      '@inertiajs/react': InertiaReact,
      '@diagonal-hq/pages': pagesSdkModule(),
      '@diagonal-hq/cli': runtimeHelpers
    }
  };
}
